/**
 * Wires `studio.model.ts` to the studio book's read and its one write.
 *
 * Calls `fetchStudioWorks` and signs the thumbnails, and `setStudioRecord`
 * to replace one record whole — the edit is PUT-shaped end to end, so a field
 * the studio blanks is removed rather than silently kept. After a save the
 * book is re-read, not patched.
 *
 * The state the model cannot hold is which card is open with its typed
 * draft, the saving flag, the refusal, and the generation counter — which
 * the register dialog also bumps through `refresh` when it adds to the list.
 */

import { useEffect, useState } from "react";
import type { StudioWork } from "@ondo/shared";

import { ApiError, fetchStudioWorks, setStudioRecord } from "../lib/api.ts";
import { signWorkThumbnails } from "../lib/workImages.ts";
import { STUDIO_COPY } from "./estate.constants.ts";
import { draftFrom, draftRefusal, toRequest, toStudioView } from "./studio.model.ts";
import type { StudioDraft, StudioView } from "./studio.model.ts";

/**
 * The studio book: one list, one card open for editing at a time.
 *
 * The edit is PUT-shaped end to end — `draftFrom` opens the form on the whole
 * record and `toRequest` sends the whole record back — so a field the studio
 * blanks is a field removed, never a field silently kept.
 */
export function useStudioController(workBase: string) {
  const [works, setWorks] = useState<StudioWork[] | null>(null);
  const [thumbs, setThumbs] = useState(new Map<string, string>());
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ workId: string; draft: StudioDraft } | null>(null);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    let active = true;

    fetchStudioWorks()
      .then(async (rows) => {
        const signed = await signWorkThumbnails(
          rows.map((work) => ({ id: work.workId, imagePath: work.imagePath })),
        );
        if (!active) return;
        setWorks(rows);
        setThumbs(signed);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setError(cause instanceof ApiError ? cause.message : STUDIO_COPY.errorTitle);
      });

    return () => {
      active = false;
    };
  }, [generation]);

  const openEdit = (workId: string) => {
    const work = works?.find((candidate) => candidate.workId === workId);
    if (!work) return;
    setRefusal(null);
    setEditing({ workId, draft: draftFrom(work) });
  };

  const editDraft = (patch: Partial<StudioDraft>) => {
    setEditing((current) =>
      current === null ? null : { ...current, draft: { ...current.draft, ...patch } },
    );
    setRefusal(null);
  };

  const save = async () => {
    if (editing === null) return;

    const stop = draftRefusal(editing.draft);
    if (stop !== null) {
      setRefusal(stop);
      return;
    }

    setSaving(true);
    try {
      await setStudioRecord(editing.workId, toRequest(editing.draft));
      setEditing(null);
      setGeneration((n) => n + 1);
    } catch (cause) {
      setRefusal(cause instanceof ApiError ? cause.message : STUDIO_COPY.errorTitle);
    } finally {
      setSaving(false);
    }
  };

  const view: StudioView = error
    ? { kind: "error", title: STUDIO_COPY.errorTitle, message: error }
    : works === null
      ? { kind: "loading", message: STUDIO_COPY.loading }
      : toStudioView(works, workBase);

  return {
    view,
    thumbs,
    editing,
    refusal,
    saving,
    openEdit,
    editDraft,
    closeEdit: () => setEditing(null),
    save,
    /** Re-read the book — after the register dialog has added to it. */
    refresh: () => setGeneration((n) => n + 1),
  };
}
